import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { TSM4_PRODUCTS } from "@/config/site";

const NAV = [
  { label: "Home", href: "/", path: "/" },
  { label: "About TSM", href: "/about", path: "/about" },
  { label: "TSM4 Solutions", href: "/tsm4", path: "/tsm4", dropdown: true },
  { label: "MITIVISE", href: "/mitivise", path: "/mitivise" },
  { label: "Workshops & Resources", href: "/workshops", path: "/workshops" },
  { label: "Contact", href: "/contact", path: "/contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setDdOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-black/5 bg-white/85 backdrop-blur-xl">
      <div className="container-tsm flex h-[76px] items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" aria-label="TSM Group CLE home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="header-nav">
          {NAV.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="relative">
                <button
                  type="button"
                  data-testid="header-nav-tsm4-solutions"
                  aria-expanded={ddOpen}
                  onClick={() => setDdOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wider text-navy/70 transition-colors hover:text-teal"
                >
                  {item.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${ddOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {ddOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setDdOpen(false)} aria-hidden="true" />
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-1/2 top-full z-50 mt-3 w-80 -translate-x-1/2 overflow-hidden rounded-lg border border-navy/10 bg-white shadow-[0_18px_50px_rgb(23,43,58,0.16)]"
                        data-testid="header-tsm4-dropdown"
                      >
                      <a href="/tsm4" className="block border-b border-navy/5 bg-paper px-5 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-white">
                        TSM4 Overview — Four Solutions. One Smarter Business.
                      </a>
                      {TSM4_PRODUCTS.map((p) => (
                        <a
                          key={p.name}
                          href={p.href}
                          data-testid={`header-tsm4-${p.href.split("/").pop()}`}
                          className="flex items-start justify-between gap-3 px-5 py-3.5 transition-colors last:border-0 hover:bg-paper"
                        >
                          <span>
                            <span className="block text-sm font-bold text-navy">{p.name}</span>
                            <span className="mt-0.5 block text-xs text-ink/55">{p.label}</span>
                          </span>
                          {p.status === "coming_soon" && (
                            <span className="mt-0.5 shrink-0 rounded-full border border-dashed border-tealdeep/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-tealdeep">
                              Coming soon
                            </span>
                          )}
                        </a>
                      ))}
                    </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={item.label}
                to={item.href}
                end={item.path === "/"}
                data-testid={`header-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-bold uppercase tracking-wider transition-colors ${
                    isActive ? "text-tealdeep" : "text-navy/70 hover:text-teal"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a href="/contact" data-testid="header-cta" className="btn-gold hidden !px-5 !py-2.5 sm:inline-flex">
            Request a Service
            <ArrowRight className="h-4 w-4" />
          </a>
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-navy/15 text-navy transition-colors hover:border-teal hover:text-teal lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-navy/5 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="container-tsm flex flex-col gap-1 py-4">
              {NAV.map((item) =>
                item.dropdown ? (
                  <div key={item.label} className="border-b border-navy/5 pb-2">
                    <button
                      type="button"
                      data-testid="mobile-nav-tsm4-solutions"
                      aria-expanded={ddOpen}
                      onClick={() => setDdOpen((v) => !v)}
                      className="flex w-full items-center justify-between rounded-sm px-2 py-3 text-sm font-bold uppercase tracking-wider text-navy/80"
                    >
                      TSM4 Solutions
                      <ChevronDown className={`h-4 w-4 transition-transform ${ddOpen ? "rotate-180" : ""}`} />
                    </button>
                    {ddOpen && (
                      <div className="mt-1 space-y-1 pl-3" data-testid="mobile-nav-tsm4-list">
                        <a href="/tsm4" onClick={() => setOpen(false)} className="block rounded-sm px-2 py-2.5 text-sm font-semibold text-tealdeep">
                          TSM4 Overview
                        </a>
                        {TSM4_PRODUCTS.map((p) => (
                          <a key={p.name} href={p.href} onClick={() => setOpen(false)} className="flex items-center justify-between rounded-sm px-2 py-2.5 text-sm font-semibold text-navy/80">
                            {p.name}
                            {p.status === "coming_soon" && <span className="font-mono text-[9px] uppercase tracking-wider text-tealdeep">Coming soon</span>}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className="rounded-sm px-2 py-3 text-sm font-bold uppercase tracking-wider text-navy/80 transition-colors hover:text-teal"
                  >
                    {item.label}
                  </a>
                ),
              )}
              <a href="/contact" data-testid="mobile-nav-cta" onClick={() => setOpen(false)} className="btn-gold mt-3 w-full">
                Request a Service
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
